import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Loader2, MailCheck } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export const EmailCard = () => {
  const { user } = useAuth();
  const [info, setInfo] = useState({ pending_email: null, needs_password: true });
  const [editing, setEditing] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/auth/email-change").then((r) => setInfo(r.data)).catch(() => {});
  }, [user.email]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/email-change", { new_email: email.trim(), password: password || null });
      setInfo((i) => ({ ...i, pending_email: data.pending_email }));
      toast.success(data.message);
      setEditing(false);
      setPassword("");
    } catch (err) {
      toast.error(errMsg(err, "Could not start the email change"));
    } finally {
      setBusy(false);
    }
  };

  const cancel = async () => {
    await api.delete("/auth/email-change");
    setInfo((i) => ({ ...i, pending_email: null }));
  };

  return (
    <div className="mm-card p-4 mb-6" data-testid="email-card">
      <div className="text-xs text-mm-muted font-semibold uppercase tracking-wider">Your email</div>
      <div className="flex items-center justify-between mt-1 gap-2">
        <span className="font-semibold break-all" data-testid="email-display">{user.email}</span>
        {!editing && (
          <button className="p-2 rounded-full hover:bg-mm-soft shrink-0" onClick={() => { setEmail(""); setEditing(true); }} data-testid="email-edit" aria-label="Change email"><Pencil size={16} /></button>
        )}
      </div>
      {info.pending_email && !editing && (
        <div className="mt-3 text-sm flex items-start gap-2 rounded-xl bg-mm-soft p-3" data-testid="email-pending">
          <MailCheck size={16} className="mt-0.5 shrink-0 text-mm-primary" />
          <span className="flex-1">Check <b>{info.pending_email}</b> for a confirmation link (valid 1 hour).</span>
          <button className="text-mm-primary font-semibold" onClick={cancel} data-testid="email-pending-cancel">Cancel</button>
        </div>
      )}
      {editing && (
        <form onSubmit={submit} className="mt-3 space-y-2" data-testid="email-form">
          <input className="mm-input" type="email" required autoFocus placeholder="New email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="email-new-input" />
          {info.needs_password && (
            <input className="mm-input" type="password" required placeholder="Current password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="email-password-input" />
          )}
          <p className="text-xs text-mm-muted" data-testid="email-google-warning">After confirming, sign in with the new email. If you use Google sign-in, use the Google account for the new address — your other devices will be signed out.</p>
          <div className="flex gap-2">
            <button className="mm-btn" disabled={busy} data-testid="email-submit">{busy ? <Loader2 size={16} className="animate-spin" /> : "Send confirmation link"}</button>
            <button type="button" className="mm-btn-ghost" onClick={() => setEditing(false)} data-testid="email-cancel">Cancel</button>
          </div>
        </form>
      )}
    </div>
  );
};
